import React, { useState, useEffect, useMemo } from "react";
import * as THREE from "three";
import { ArrowLeft, ArrowRight, Box, MapPin } from "lucide-react";
import { useBodies, useCameras, useSceneActions, useSites } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { usePanelStore } from "../../stores/panelStore";
import { useObjectRegistry } from "../../runtime/objectRegistry";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import TransformGizmoHeaderActions from "./TransformGizmoHeaderActions";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import Separator from "../ui/Separator";
import AxisNumberInput from "../ui/AxisNumberInput";
import DragNumberInput from "../ui/DragNumberInput";
import FieldLabel from "../ui/FieldLabel";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import { InspectorVisibilitySection } from "./InspectorVisibilitySection";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import { InspectorVisibilityEyeButton } from "../ui/InspectorVisibilityEyeButton";
import InfoTooltip from "../ui/InfoTooltip";
import MenuSelect from "../ui/MenuSelect";
import { useViewerActions, useViewerStore } from "../../stores/viewerStore";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import { dispatchViewportCameraVisibility } from "../draggable-panels/scene-inventory/sceneInventoryViewportEvents";

const AXES = ["X", "Y", "Z"];

const formatCoord = (v) => (Number.isFinite(v) ? v.toFixed(4) : "0.0000");
const toWxyz = (q) => [Number(q.w), Number(q.x), Number(q.y), Number(q.z)];

function quatToEulerDeg(quat) {
  const [w, x, y, z] = Array.isArray(quat) && quat.length === 4 ? quat : [1, 0, 0, 0];
  const q = new THREE.Quaternion(
    Number(x) || 0,
    Number(y) || 0,
    Number(z) || 0,
    Number(w) || 1
  ).normalize();
  const e = new THREE.Euler().setFromQuaternion(q, "XYZ");
  return [
    THREE.MathUtils.radToDeg(e.x),
    THREE.MathUtils.radToDeg(e.y),
    THREE.MathUtils.radToDeg(e.z),
  ];
}

function eulerDegToQuat(deg) {
  const q = new THREE.Quaternion().setFromEuler(
    new THREE.Euler(
      THREE.MathUtils.degToRad(Number(deg[0]) || 0),
      THREE.MathUtils.degToRad(Number(deg[1]) || 0),
      THREE.MathUtils.degToRad(Number(deg[2]) || 0),
      "XYZ"
    )
  );
  return [Number(q.w), Number(q.x), Number(q.y), Number(q.z)];
}

const CameraPropertiesPanel = ({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
}) => {
  const selectedCamera = useSelectionStore((s) => s.selectedCamera);
  const cameras = useCameras();
  const bodies = useBodies();
  const sites = useSites();
  const sceneObjects = useObjectRegistry() || {};
  const sceneActions = useSceneActions();
  const selectBody = useSelectionStore((s) => s.selectBody);
  const selectSite = useSelectionStore((s) => s.selectSite);
  const setLastSelectionSource = useSelectionStore((s) => s.setLastSelectionSource);
  const _setSceneGraphOpen = usePanelStore((s) => s.actions.setSceneGraphOpen);
  const cameraMarkerVisibility = useViewerStore((s) => s.cameraMarkerVisibility);
  const viewerActions = useViewerActions();

  const cam = selectedCamera ? cameras?.[selectedCamera] : null;

  const [eulerDeg, setEulerDeg] = useState([0, 0, 0]);
  const [editingInputs, setEditingInputs] = useState({});
  const orientationFingerprint = useMemo(
    () => (cam && Array.isArray(cam.orientation) ? cam.orientation.join(":") : ""),
    [cam]
  );

  useEffect(() => {
    if (!cam || !selectedCamera) return;
    setEulerDeg(quatToEulerDeg(cam.orientation));
  }, [selectedCamera, orientationFingerprint, cam]);

  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformEditorBlock.disabled,
      enableShortcuts: true,
    });
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();

  const bodyOptions = useMemo(
    () =>
      Object.values(bodies || {})
        .map((b) => ({ uuid: b.uuid, label: b.name || b.uuid }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [bodies]
  );

  const siteOptions = useMemo(
    () =>
      Object.values(sites || {})
        .map((s) => ({ uuid: s.uuid, label: s.name || s.uuid }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [sites]
  );

  const canMountBody = bodyOptions.length > 0;
  const canMountSite = siteOptions.length > 0;
  const hasMountTargets = canMountBody || canMountSite;

  useEffect(() => {
    if (!selectedCamera || !cam) return;
    if (cam.kind !== "mounted") return;
    if (!hasMountTargets) {
      sceneActions.updateCamera(selectedCamera, {
        kind: "fixed",
        mountType: undefined,
        mountBodyUuid: undefined,
        mountSiteUuid: undefined,
      });
      return;
    }
    if (!canMountSite && cam.mountType === "site" && canMountBody) {
      sceneActions.updateCamera(selectedCamera, {
        mountType: "body",
        mountSiteUuid: undefined,
      });
      return;
    }
    if (!canMountBody && cam.mountType === "body" && canMountSite) {
      sceneActions.updateCamera(selectedCamera, {
        mountType: "site",
        mountBodyUuid: undefined,
      });
    }
  }, [
    selectedCamera,
    cam,
    cam?.kind,
    cam?.mountType,
    hasMountTargets,
    canMountBody,
    canMountSite,
    sceneActions,
  ]);

  if (!cam) return null;

  const thisCameraMarkerVisible =
    selectedCamera != null ? (cameraMarkerVisibility[selectedCamera] ?? true) : true;

  const update = (patch) => sceneActions.updateCamera(selectedCamera, patch);
  const toggleSelectedCameraMarkerVisibility = () => {
    if (!selectedCamera) return;
    const nextVisible = !thisCameraMarkerVisible;
    viewerActions.setCameraMarkerVisibility(selectedCamera, nextVisible);
    if (nextVisible) dispatchViewportCameraVisibility(true);
  };

  const effectiveMountSource =
    canMountSite && !canMountBody
      ? "site"
      : canMountBody && !canMountSite
        ? "body"
        : cam.mountType === "site" && canMountSite
          ? "site"
          : "body";

  const cameraModeValue = cam.kind === "mounted" && hasMountTargets ? "mounted" : "fixed";
  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";
  const mountedTargetUuid =
    cameraModeValue === "mounted"
      ? effectiveMountSource === "site"
        ? cam.mountSiteUuid
        : cam.mountBodyUuid
      : null;
  const mountedTargetName =
    effectiveMountSource === "site"
      ? sites?.[mountedTargetUuid]?.name
      : bodies?.[mountedTargetUuid]?.name;
  const mountedTargetLabel =
    effectiveMountSource === "site"
      ? mountedTargetUuid
        ? `Inspect Mount Site: ${stripPrefix(mountedTargetName) || mountedTargetUuid}`
        : "No mount site"
      : mountedTargetUuid
        ? `Inspect Mount Body: ${stripPrefix(mountedTargetName) || mountedTargetUuid}`
        : "No mount body";
  const _quickNavActions = [
    {
      key: "back",
      label: canGoBack ? `Back (ALT + <-): ${quickNavBackTarget.label}` : "Back (ALT + <-)",
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: canGoForward
        ? `Forward (ALT + ->): ${quickNavForwardTarget.label}`
        : "Forward (ALT + ->)",
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    cameraModeValue === "mounted"
      ? {
          key: "mountTarget",
          label: mountedTargetLabel,
          icon:
            effectiveMountSource === "site" ? (
              <MapPin className="w-4 h-4" aria-hidden="true" />
            ) : (
              <Box className="w-4 h-4" aria-hidden="true" />
            ),
          disabled: !mountedTargetUuid,
          onDefaultClick: () => {
            if (!mountedTargetUuid) return;
            setLastSelectionSource("sceneTree");
            if (effectiveMountSource === "site") {
              selectSite(mountedTargetUuid);
            } else {
              selectBody(mountedTargetUuid);
            }
            requestInspectorChromeFlash();
          },
        }
      : null,
  ].filter(Boolean);

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <InspectorVisibilitySection
          sectionTitle="Camera Visibility"
          headerActions={
            <div className="flex items-center gap-1">
              <InspectorVisibilityEyeButton
                visible={thisCameraMarkerVisible}
                onToggle={toggleSelectedCameraMarkerVisibility}
                tooltipWhenOn="This camera's scene marker is visible — click to hide"
                tooltipWhenOff="This camera's scene marker is hidden — click to show"
                ariaLabel="Toggle this camera marker visibility"
              />
            </div>
          }
        />
      </div>
    );
  }

  const handlePositionChange = (idx, val) => {
    const next = Array.isArray(cam.position) ? [...cam.position] : [0, 0, 0];
    next[idx] = Number.isFinite(Number(val)) ? Number(val) : next[idx];
    update({ position: next });
  };

  const handleEulerChange = (idx, val) => {
    const next = [...eulerDeg];
    next[idx] = Number.isFinite(Number(val)) ? Number(val) : next[idx];
    setEulerDeg(next);
    update({ orientation: eulerDegToQuat(next) });
  };

  const renderPosAxis = (idx) => {
    const key = `pos-${idx}`;
    const storeVal = Number((cam.position ?? [0, 0, 0])[idx] ?? 0);
    const displayVal =
      editingInputs[key] !== undefined ? editingInputs[key] : formatCoord(storeVal);
    return (
      <AxisNumberInput
        key={key}
        axis={AXES[idx]}
        value={displayVal}
        dragValue={storeVal}
        onDragValueChange={(v) => handlePositionChange(idx, v)}
        onChange={(e) => {
          setEditingInputs((p) => ({ ...p, [key]: e.target.value }));
          handlePositionChange(idx, e.target.value);
        }}
        onFocus={(e) => setEditingInputs((p) => ({ ...p, [key]: e.target.value }))}
        onBlur={(e) => {
          handlePositionChange(idx, e.target.value);
          setEditingInputs((p) => {
            const n = { ...p };
            delete n[key];
            return n;
          });
        }}
        labelClassName="text-xs panel-text-helper uppercase w-3 text-center"
        inputClassName="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-100 text-xs"
        inputProps={{ step: "0.01" }}
      />
    );
  };

  const renderEulerAxis = (idx) => {
    const key = `euler-${idx}`;
    const storeVal = eulerDeg[idx] ?? 0;
    const displayVal =
      editingInputs[key] !== undefined ? editingInputs[key] : formatCoord(storeVal);
    return (
      <AxisNumberInput
        key={key}
        axis={`${AXES[idx]}°`}
        value={displayVal}
        dragValue={storeVal}
        onDragValueChange={(v) => handleEulerChange(idx, v)}
        onChange={(e) => {
          setEditingInputs((p) => ({ ...p, [key]: e.target.value }));
          handleEulerChange(idx, e.target.value);
        }}
        onFocus={(e) => setEditingInputs((p) => ({ ...p, [key]: e.target.value }))}
        onBlur={(e) => {
          handleEulerChange(idx, e.target.value);
          setEditingInputs((p) => {
            const n = { ...p };
            delete n[key];
            return n;
          });
        }}
        labelClassName="text-xs panel-text-helper w-5 text-center"
        inputClassName="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-100 text-xs"
        dragSensitivity={0.2}
        inputProps={{ step: "0.1" }}
      />
    );
  };

  const renderScalar = (key, label, value, onCommit, { step = "1", dragSensitivity = 1 } = {}) => {
    const numVal = Number.isFinite(Number(value)) ? Number(value) : 0;
    const displayVal = editingInputs[key] !== undefined ? editingInputs[key] : String(numVal);
    return (
      <DragNumberInput
        label={label}
        value={displayVal}
        dragValue={numVal}
        onDragValueChange={onCommit}
        onChange={(e) => {
          setEditingInputs((p) => ({ ...p, [key]: e.target.value }));
          onCommit(e.target.value);
        }}
        onFocus={(e) => setEditingInputs((p) => ({ ...p, [key]: e.target.value }))}
        onBlur={(e) => {
          onCommit(e.target.value);
          setEditingInputs((p) => {
            const n = { ...p };
            delete n[key];
            return n;
          });
        }}
        wrapperClassName="space-y-1"
        labelClassName="text-[10px] panel-text-helper uppercase"
        inputClassName="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-100 text-xs"
        dragSensitivity={dragSensitivity}
        inputProps={{ step }}
      />
    );
  };

  const isMounted = cam.kind === "mounted";

  const handleSnapToCurrentView = () => {
    const viewerCamera = getViewerHandles()?.camera;
    if (!viewerCamera) return;
    viewerCamera.updateMatrixWorld?.(true);

    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    viewerCamera.getWorldPosition(worldPos);
    viewerCamera.getWorldQuaternion(worldQuat);

    // Fixed/world cameras store world-space pose directly.
    if (cam.kind !== "mounted") {
      update({
        position: [worldPos.x, worldPos.y, worldPos.z],
        orientation: toWxyz(worldQuat),
      });
      setEulerDeg(quatToEulerDeg(toWxyz(worldQuat)));
      return;
    }

    if (effectiveMountSource === "site") {
      const site = sites?.[cam.mountSiteUuid] || null;
      if (!site) return;
      const parentBodyUuid =
        Object.values(bodies || {}).find(
          (body) => Array.isArray(body?.sites) && body.sites.includes(cam.mountSiteUuid)
        )?.uuid || null;
      if (!parentBodyUuid) return;
      const parentObj = sceneObjects?.[parentBodyUuid] || null;
      if (!parentObj) return;

      parentObj.updateMatrixWorld?.(true);
      const bodyPos = new THREE.Vector3();
      const bodyQuat = new THREE.Quaternion();
      parentObj.getWorldPosition(bodyPos);
      parentObj.getWorldQuaternion(bodyQuat);

      const siteLocalPos = new THREE.Vector3(
        Number(site.position?.[0]) || 0,
        Number(site.position?.[1]) || 0,
        Number(site.position?.[2]) || 0
      );
      const siteLocalWxyz =
        Array.isArray(site.orientation) && site.orientation.length === 4
          ? site.orientation
          : [1, 0, 0, 0];
      const siteLocalQuat = new THREE.Quaternion(
        Number(siteLocalWxyz[1]) || 0,
        Number(siteLocalWxyz[2]) || 0,
        Number(siteLocalWxyz[3]) || 0,
        Number(siteLocalWxyz[0]) || 1
      );

      const siteWorldPos = siteLocalPos.applyQuaternion(bodyQuat).add(bodyPos);
      const siteWorldQuat = bodyQuat.clone().multiply(siteLocalQuat);
      const invSiteQuat = siteWorldQuat.clone().invert();
      const localPos = worldPos.clone().sub(siteWorldPos).applyQuaternion(invSiteQuat);
      const localQuat = invSiteQuat.multiply(worldQuat.clone()).normalize();
      const localWxyz = toWxyz(localQuat);

      update({
        position: [localPos.x, localPos.y, localPos.z],
        orientation: localWxyz,
      });
      setEulerDeg(quatToEulerDeg(localWxyz));
      return;
    }

    const parentBodyObj = sceneObjects?.[cam.mountBodyUuid] || null;
    if (!parentBodyObj) return;
    parentBodyObj.updateMatrixWorld?.(true);
    const bodyPos = new THREE.Vector3();
    const bodyQuat = new THREE.Quaternion();
    parentBodyObj.getWorldPosition(bodyPos);
    parentBodyObj.getWorldQuaternion(bodyQuat);

    const invBodyQuat = bodyQuat.clone().invert();
    const localPos = worldPos.clone().sub(bodyPos).applyQuaternion(invBodyQuat);
    const localQuat = invBodyQuat.multiply(worldQuat.clone()).normalize();
    const localWxyz = toWxyz(localQuat);
    update({
      position: [localPos.x, localPos.y, localPos.z],
      orientation: localWxyz,
    });
    setEulerDeg(quatToEulerDeg(localWxyz));
  };

  return (
    <div className="p-3 space-y-4">
      <InspectorVisibilitySection
        sectionTitle="Camera Visibility"
        headerActions={
          <div className="flex items-center gap-1">
            <InspectorVisibilityEyeButton
              visible={thisCameraMarkerVisible}
              onToggle={toggleSelectedCameraMarkerVisibility}
              tooltipWhenOn="This camera's scene marker is visible — click to hide"
              tooltipWhenOff="This camera's scene marker is hidden — click to show"
              ariaLabel="Toggle this camera marker visibility"
            />
          </div>
        }
      />

      <Separator />

      <div>
        <FieldLabel as="label" className="block mb-1">
          Camera Mode
        </FieldLabel>
        <MenuSelect
          size="compact"
          value={cameraModeValue}
          onChange={(next) => {
            if (next === "fixed") {
              update({
                kind: "fixed",
                mountType: undefined,
                mountBodyUuid: undefined,
                mountSiteUuid: undefined,
              });
              return;
            }
            const mountType = canMountSite && !canMountBody ? "site" : "body";
            const patch = { kind: "mounted", mountType };
            if (mountType === "body" && bodyOptions[0]) {
              patch.mountBodyUuid = bodyOptions[0].uuid;
              patch.mountSiteUuid = undefined;
            } else if (mountType === "site" && siteOptions[0]) {
              patch.mountSiteUuid = siteOptions[0].uuid;
              patch.mountBodyUuid = undefined;
            }
            update(patch);
          }}
          options={[
            { value: "fixed", label: "Fixed (world)" },
            ...(hasMountTargets ? [{ value: "mounted", label: "Mounted to body/site" }] : []),
          ]}
          triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
          aria-label="Camera mode"
        />
      </div>

      {isMounted && hasMountTargets && (
        <>
          {canMountBody && canMountSite ? (
            <div>
              <FieldLabel as="label" className="block mb-1">
                Mount Source
              </FieldLabel>
              <MenuSelect
                size="compact"
                value={effectiveMountSource}
                onChange={(next) => {
                  if (next === "site") {
                    update({
                      mountType: "site",
                      mountSiteUuid: siteOptions[0]?.uuid,
                      mountBodyUuid: undefined,
                    });
                  } else {
                    update({
                      mountType: "body",
                      mountBodyUuid: bodyOptions[0]?.uuid,
                      mountSiteUuid: undefined,
                    });
                  }
                }}
                options={[
                  { value: "body", label: "Body" },
                  { value: "site", label: "Site" },
                ]}
                triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                aria-label="Camera mount source"
              />
            </div>
          ) : null}
          {effectiveMountSource === "site" ? (
            <div>
              <FieldLabel as="label" className="block mb-1">
                Site
              </FieldLabel>
              <MenuSelect
                size="compact"
                value={cam.mountSiteUuid || ""}
                onChange={(v) => update({ mountSiteUuid: v })}
                options={siteOptions.map((o) => ({ value: o.uuid, label: o.label }))}
                contentClassName="max-h-[min(240px,50vh)]"
                triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                aria-label="Mount site"
              />
            </div>
          ) : (
            <div>
              <FieldLabel as="label" className="block mb-1">
                Body
              </FieldLabel>
              <MenuSelect
                size="compact"
                value={cam.mountBodyUuid || ""}
                onChange={(v) => update({ mountBodyUuid: v })}
                options={bodyOptions.map((o) => ({ value: o.uuid, label: o.label }))}
                contentClassName="max-h-[min(240px,50vh)]"
                triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                aria-label="Mount body"
              />
            </div>
          )}
        </>
      )}

      <Separator />

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger
            quiet
            aria-label="Toggle Transform"
            actions={
              <TransformGizmoHeaderActions
                disabled={transformEditorBlock.disabled}
                disabledTitle={transformEditorBlock.title}
                showPositionGizmo={showPositionGizmo}
                showRotationGizmo={showRotationGizmo}
                onTogglePositionGizmo={togglePositionGizmo}
                onToggleRotationGizmo={toggleRotationGizmo}
              />
            }
          >
            <span className="min-w-0 truncate text-left">Transform</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 space-y-4">
              <div className="property-axis-grid-container">
                <FieldLabel as="label" className="block mb-1">
                  {isMounted ? "Local Position Offset (XYZ)" : "World Position (XYZ)"}
                </FieldLabel>
                <button
                  type="button"
                  onClick={handleSnapToCurrentView}
                  className="mb-2 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-100 hover:border-gray-500 transition-colors"
                >
                  Snap to current view
                </button>
                <div className="property-axis-grid grid gap-2">{[0, 1, 2].map(renderPosAxis)}</div>
              </div>

              <div className="property-axis-grid-container">
                <FieldLabel as="label" className="block mb-1">
                  {isMounted ? "Local Orientation (Euler °)" : "World Orientation (Euler °)"}
                </FieldLabel>
                <div className="property-axis-grid grid gap-2">
                  {[0, 1, 2].map(renderEulerAxis)}
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>

      <Separator />
      <div>
        <FieldLabel as="div" className="inline-flex items-center gap-1.5 mb-1">
          Stream Settings
          <InfoTooltip
            text="Off-screen render settings for this camera. FOV is vertical field of view in degrees; W/H are output pixel dimensions; FPS is the capture rate, independent of the physics simulation timestep."
            widthClass="w-72"
          />
        </FieldLabel>
        <div className="grid grid-cols-4 gap-2">
          {renderScalar(
            "fovy",
            "FOV",
            cam.fovy ?? 65,
            (v) => update({ fovy: Math.max(10, Math.min(150, Number(v) || 65)) }),
            { step: "1" }
          )}
          {renderScalar(
            "width",
            "W",
            cam.width ?? 800,
            (v) => update({ width: Math.max(32, Math.floor(Number(v) || 800)) }),
            { step: "1" }
          )}
          {renderScalar(
            "height",
            "H",
            cam.height ?? 800,
            (v) => update({ height: Math.max(32, Math.floor(Number(v) || 800)) }),
            { step: "1" }
          )}
          {renderScalar(
            "fps",
            "FPS",
            cam.fps ?? 8,
            (v) => update({ fps: Math.max(1, Math.min(30, Math.floor(Number(v) || 8))) }),
            { step: "1" }
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(CameraPropertiesPanel);
